<script>
  import { Money } from "v-money"
  import Breadcrumbs from "@/core/components/Breadcrumbs"
  import TariffCoefficientService from "@/domains/lines/services/TariffCoefficientService"

  export default {
    components: {
      Breadcrumbs,
      Money,
    },
    props: {
      id: { required: true },
    },
    data() {
      return {
        // Definição das trilhas de navegação (breadcrumbs)
        crumbs: [
          { name: "Início", path: "index" },
          { name: "Linhas", path: "lines.index" },
          { name: "Coeficiente Tarifário", path: "lines.tariffCoefficient" },
          {
            name: "Ajuste de Coeficiente Tarifário",
            path: "lines.tariffCoefficient.view" + this.id,
          },
        ],
        // Objeto que armazenará os dados do coeficiente tarifário
        tariffCoefficient: {
          cod: "",
          grupo: "",
          dataAjuste: "",
          tarifaMinima: "",
          arredondarTarifa: null,
          processoSgpe: "",
          resolucao: "",
          dataVigencia: "",
          dataFimVigencia: "",
        },
        currentSection: "FirstPage", // Seção atual da interface
        naoPatamarizados: [], // Lista de tarifas não patamarizadas
        patamares: [], // Lista de tarifas patamarizadas
        money: {
          decimal: ",",
          thousands: ".",
          prefix: "R$ ",
          precision: 5,
          disabled: true,
        }, // Parametros para formatação de valores não patamarizados
        moneyTwoDecimals: {
          decimal: ",",
          thousands: ".",
          prefix: "R$ ",
          precision: 2,
          disabled: true,
        }, // Parametros para formatação de valores patamarizados
      }
    },
    computed: {
      // Retorna um objeto com os dados do coeficiente tarifário, exceto os campos ocultos
      filteredTariffCoefficient() {
        const hiddenFields = ["flsituacao", "dtalteracao"]
        const result = Object.keys(this.tariffCoefficient)
          .filter((key) => !hiddenFields.includes(key))
          .reduce((obj, key) => {
            // Formata as datas e horas para o formato dd/mm/yyyy HH:mm:ss
            if (
              ["dtcadastro", "dtfimvalidade", "dtiniciovalidade"].includes(key)
            ) {
              obj[key] = this.formatDateTime(this.tariffCoefficient[key])
            } else if (key === "nutransportadora") {
              const transportadoraName =
                this.tariffCoefficient.deter_registry?.organization
                  ?.business_name || ""
              obj[key] =
                `${this.tariffCoefficient[key]} - ${transportadoraName}`
            } else if (key === "vlarredondamentotarifa") {
              obj[key] = `R$ ${this.formatDecimal(this.tariffCoefficient[key])}`
            } else {
              obj[key] = this.tariffCoefficient[key]
            }
            return obj
          }, {})
        return result
      },
    },
    methods: {
      async getTariffCoefficient(id) {
        const service = new TariffCoefficientService()

        const response = await service.getTariffCoefficient(id)

        if (response) {
          this.tariffCoefficient = {
            cod: response.idcoeficientetarifa,
            grupo: response.deter_registry.organization.name,
            dataAjuste: response.dtalteracao || response.dtcadastro,
            arredondarTarifa: response.vlarredondamentotarifa,
            processoSgpe: response.deprocesso,
            resolucao: response.deresolucao,
            dataVigencia: response.dtiniciovalidade,
            dataFimVigencia: response.dtfimvalidade,
          }

          response.valores.forEach((v) => {
            if (v.nutipopreco === 7) {
              this.patamares.push({
                descricao: "Patamar " + v.nuordemtipopreco,
                valor: v.vltarifa.slice(0, -3),
              })
            } else {
              this.naoPatamarizados.push({
                descricao: v.tipo.nmtipopreco,
                valor: v.vltarifa,
              })
            }
          })
        }
      },
      toggleList(section) {
        // Ensure the section is updated correctly
        this.currentSection = section
      },
      getLabel(key) {
        const labels = {
          cod: "Código",
          grupo: "Empresa/Grupo/Transportadora",
          dataAjuste: "Data e hora do Ajuste",
          processoSgpe: "Processo SGPE",
          resolucao: "Resolução",
          arredondarTarifa: "Arredondar Tarifa",
          dataVigencia: "Data de Vigência",
          dataFimVigencia: "Fim da Vigência",
        }
        return labels[key] || key.replace(/([A-Z])/g, " $1")
      },
      formatDateTime(dateTime) {
        if (!dateTime) return ""
        const [date, time] = dateTime.split(" ")
        const [year, month, day] = date.split("-")
        return `${day}/${month}/${year} ${time}`
      },
      formatDecimal(value) {
        if (value === null || value === undefined) return ""
        return parseFloat(value).toFixed(2).replace(".", ",")
      },
    },
    async mounted() {
      // Busca os dados do coeficiente tarifário e seus valores ao montar o componente
      if (this.id) {
        await this.getTariffCoefficient(this.id)
      }
    },
  }
</script>

<template>
  <div class="w-full bg-gray-50 p-6 rounded-lg shadow-lg">
    <Breadcrumbs class="mb-4" :crumbs="crumbs"/>
    <h1 class="text-xl font-bold text-blue-700 uppercase mb-4">
      Ajuste de Coeficiente Tarifário
    </h1>

    <div
      class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 bg-white p-4 rounded-md shadow"
    >
      <div
        v-for="(value, key) in filteredTariffCoefficient"
        :key="key"
        class="flex flex-col"
      >
        <label class="text-sm font-semibold text-gray-600">
          {{ getLabel(key) }}
        </label>
        <div class="text-gray-800 font-medium">{{ value }}</div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-2 text-lg mb-1 w-full">
      <button
        @click="toggleList('FirstPage')"
        :class="{
                    'selected-button': currentSection === 'FirstPage',
                    'non-selected-button': currentSection !== 'FirstPage'
                }"
        class="col-span-1 text-center content-center h-16 uppercase shadow-md"
      >
        Não Patamarizadas
      </button>
      <button
        @click="toggleList('SecondPage')"
        :class="{
                    'selected-button': currentSection === 'SecondPage',
                    'non-selected-button': currentSection !== 'SecondPage'
                }"
        class="col-span-1 text-center content-center h-16 uppercase shadow-md"
      >
        Patamarizadas
      </button>
    </div>

    <!-- Table for Não Patamarizadas -->
    <div v-if="currentSection === 'FirstPage'" class="shadow-md p-4 col-span-6">
      <table class="table-auto w-full border-collapse border border-gray-300">
        <thead>
          <tr class="bg-gray-200">
            <!-- <th class="border border-gray-300 px-4 py-2">Código</th> -->
            <th class="border border-gray-300 px-4 py-2">Descrição</th>
            <th class="border border-gray-300 px-4 py-2">Valor</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in naoPatamarizados"
            :key="item?.descricao || 'unknown'"
            class="hover:bg-gray-100"
          >
            <!-- <td class="border border-gray-300 px-4 py-2">{{ item?.codigo || 'N/A' }}</td> -->
            <td class="border border-gray-300 px-4 py-2">
              {{ item?.descricao || 'N/A' }}
            </td>
            <td class="border border-gray-300 px-4 py-2">
              <money v-model="item.valor" v-bind="money"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Table for Patamarizadas -->
    <div
      v-if="currentSection === 'SecondPage'"
      class="shadow-md p-4 col-span-6"
    >
      <table class="table-auto w-full border-collapse border border-gray-300">
        <thead>
          <tr class="bg-gray-200">
            <!-- <th class="border border-gray-300 px-4 py-2">Código</th> -->
            <th class="border border-gray-300 px-4 py-2">Descrição</th>
            <th class="border border-gray-300 px-4 py-2">Valor</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in patamares"
            :key="item?.descricao || 'unknown'"
            class="hover:bg-gray-100"
          >
            <!-- <td class="border border-gray-300 px-4 py-2">{{ item?.codigo || 'N/A' }}</td> -->
            <td class="border border-gray-300 px-4 py-2">
              {{ item?.descricao || 'N/A' }}
            </td>
            <td class="border border-gray-300 px-4 py-2">
              <money v-model="item.valor" v-bind="moneyTwoDecimals"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
  button:focus {
    outline: 0 !important;
  }

  .selected-button {
    color: white;
    background-image: linear-gradient(to right, #38c172, #029e4d);
  }

  .non-selected-button {
    opacity: 0.5;
    background-image: linear-gradient(to right, #b4e5c7, #38c172);
  }
</style>
